import { Component, Injector, Input } from '@angular/core';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import {
    CoachingTranscript,
    TranscriptFocus,
} from '../../../../coaching-base/general';
import { TagsDialogResult } from '../../../../coaching-tags/tags-dialog-result.model';
import { TagsComponent } from '../../../../coaching-tags/tags/tags.component';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../../common/take-until-destroy.decorator';
import { useDispatcher } from '../../../../store.decorator';
import { createTagsDialogData } from '../../function/create-tags-dialog-data.function';
import { BehaviourTag } from '../../model/behaviour-tag.model';
import { editNotesFocus } from '../../state/edit-notes-focus.action';
import { updateTranscript } from '../../state/update-transcript.action';
import { TranscriptDialogService } from '../../transcript-dialog.service';
import { BehaviourSelectionDialogComponent } from '../behaviour-selection-dialog/behaviour-selection-dialog.component';

import { TranslationKeyMap } from './focus.i18n';

@Component({
    selector: 'cmps-transcript-dialog-focus',
    templateUrl: './focus.component.html',
})
@TakeUntilDestroy()
export class FocusComponent {
    @Input() forNotes: boolean;

    /** @private */ _i18n = TranslationKeyMap;

    constructor(
        private readonly _injector: Injector,
        /** @private */ readonly _service: TranscriptDialogService,
        private readonly _dialog: CmpsDialog,
    ) {}

    /** @private */
    _onEditFocusClick(): void {
        const data = createTagsDialogData(
            this._service.state.transcript?.attendees,
            this._service.state.transcript?.focuses,
        );
        const focusDialogRef: CmpsDialogRef<TagsComponent, TagsDialogResult> =
            this._dialog.open(TagsComponent, {
                injector: this._injector,
                data,
            });
        focusDialogRef.result
            .pipe(
                tap((ret) => {
                    if (ret == null) {
                        return;
                    }
                    const nextFocuses: TranscriptFocus[] = [
                        {
                            behaviourId: ret.selected.behaviourId,
                            subBehaviourId: ret.selected.subBehaviourId,
                            isPrimary: true,
                        },
                    ];
                    useDispatcher(
                        this._service,
                        updateTranscript({
                            transcript: {
                                ...this._service.state.transcript,
                                focuses: nextFocuses,
                            },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onEditNotesFocusClick(): void {
        // TODO revise. cmps-tag-set-trigger and formControlName may be used in the future
        const data = createTagsDialogData(
            this._service.state.transcript?.attendees,
            this._service.state.transcript?.focuses,
        );
        const focusDialogRef: CmpsDialogRef<TagsComponent, TagsDialogResult> =
            this._dialog.open(TagsComponent, {
                injector: this._injector,
                data,
            });
        focusDialogRef.result
            .pipe(
                tap((ret) => {
                    if (ret == null) {
                        return;
                    }
                    useDispatcher(
                        this._service,
                        editNotesFocus({
                            focus: {
                                behaviourId: ret.selected.behaviourId,
                                subBehaviourId: ret.selected.subBehaviourId,
                            },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */
    _onRemoveBehaviourClick(value: BehaviourTag): void {
        const nextFocuses: TranscriptFocus[] = [];
        const primary = this._service.state.transcript.focuses.find(
            (e) => e.isPrimary === true,
        );
        if (primary != null) {
            nextFocuses.push(primary);
        }
        nextFocuses.push(
            ...this._service.state.behaviourTags
                .filter(
                    (e) =>
                        e !== value &&
                        (primary == null ||
                            e.behaviour.id !== primary.behaviourId),
                )
                .map<TranscriptFocus>((e) => ({
                    behaviourId: e.behaviour.id,
                })),
        );
        useDispatcher(
            this._service,
            updateTranscript({
                transcript: {
                    ...this._service.state.transcript,
                    focuses: nextFocuses,
                },
            }),
        );
    }

    /** @private */
    _onAddBehaviourClick(): void {
        const data: CoachingTranscript = this._service.state.transcript;
        const ref = this._dialog.open<
            BehaviourSelectionDialogComponent,
            CoachingTranscript,
            BehaviourTag[]
        >(BehaviourSelectionDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret == null) {
                        return;
                    }
                    const nextFocuses: TranscriptFocus[] = [];
                    const primary = this._service.state.transcript.focuses.find(
                        (e) => e.isPrimary === true,
                    );
                    if (primary != null) {
                        nextFocuses.push(primary);
                    }
                    nextFocuses.push(
                        ...ret
                            .filter(
                                (e) =>
                                    e.isChecked &&
                                    (primary == null ||
                                        e.behaviour.id !== primary.behaviourId),
                            )
                            .map<TranscriptFocus>((e) => ({
                                behaviourId: e.behaviour.id,
                            })),
                    );
                    useDispatcher(
                        this._service,
                        updateTranscript({
                            transcript: {
                                ...this._service.state.transcript,
                                focuses: nextFocuses,
                            },
                        }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
